import { computeRetrogradeBands, bandColorsForPlanet } from '../utils/retrogradeBands';
import { annotateBandCongestion, lightenStroke } from '../utils/bandDensity';
import { abbreviatePlanet } from '../utils/planetAbbreviations';

// addAreaHighlightOverlaysUtil.js
// Utility for rendering overlays and updating the Message Panel for area highlight studies, given just date pairs and params.

/**
 * Adds overlays (area highlights, vertical lines, trendlines) for each date pair and updates the Messages Panel.
 * @param {Object} opts
 * @param {Array<{startDate: string, endDate: string}>} opts.datePairs - List of date pairs
 * @param {Array<string>} opts.summaryLines - Lines for the Summary tab
 * @param {Array<string>} opts.futureDates - Lines for the Future Dates tab
 * @param {Object} opts.params - Overlay params (color, label, etc.)
 * @param {Function} opts.addOverlay - Overlay context function
 * @param {Object} opts.barData - { dates, ohlc }
 * @param {Object} [opts.extendedBarData] - { dates, ohlc }
 * @param {Function} [opts.updateMessagesPanel] - Callback to update the Messages Panel
 */
export function addAreaHighlightOverlaysAndMessages({ datePairs, summaryLines, futureDates, params = {}, addOverlay, barData, extendedBarData, updateMessagesPanel, onJournalAppend, isAdmin = false }) {
  if (!Array.isArray(datePairs) || typeof addOverlay !== 'function') return;
  const overlayData = extendedBarData && extendedBarData.dates && extendedBarData.dates.length > 0 ? extendedBarData : barData;
  if (!overlayData || !Array.isArray(overlayData.dates) || !Array.isArray(overlayData.ohlc)) return;
  
  console.log('[OverlayDebug] addAreaHighlightOverlaysAndMessages called with datePairs:', JSON.stringify(datePairs, null, 2));
  // Helper: Snap date to nearest in overlayData.dates
  function snapToNearestDate(date) {
    if (!date) return date;
    if (overlayData.dates.includes(date)) return date;
    const datePart = date.split('T')[0];
    if (overlayData.dates.includes(datePart)) return datePart;
    // Find nearest date
    let minDiff = Infinity, nearest = null;
    for (const d of overlayData.dates) {
      const diff = Math.abs(new Date(d) - new Date(date));
      if (diff < minDiff) { minDiff = diff; nearest = d; }
    }
    if (nearest) {
      console.warn(`[OverlayDebug] Snapping date '${date}' to nearest available date '${nearest}' in overlayData.dates`);
      return nearest;
    }
    console.warn(`[OverlayDebug] Could not snap date '${date}' to any in overlayData.dates`);
    return date;
  }

  // 'lines' draws each window as a single open-to-close line instead of a shaded band. The
  // vertical boundary markers are kept in both styles: they mark where the window starts and ends,
  // which the line alone does not, and they are not the "filled vertical bands" being replaced.
  const linesOnly = params.displayStyle === 'lines';

  // Overlay rendering
  datePairs.forEach((pair, idx) => {
    // Support both array and object formats for date pairs
    let startDate, endDate;
    if (Array.isArray(pair)) {
      [startDate, endDate] = pair;
    } else if (pair && typeof pair === 'object') {
      startDate = pair.startDate || pair.date1;
      endDate = pair.endDate || pair.date2;
    }
    if (!startDate || !endDate) return;
    startDate = snapToNearestDate(startDate);
    endDate = snapToNearestDate(endDate);
    const label = params.label || `Strategy Score Study`;
    // Use color from params or default palette inline in overlay objects
    const overlayColor = params.color || defaultColors[idx % defaultColors.length];
    console.log(`[OverlayDebug] Creating overlays for pair: {startDate: ${startDate}, endDate: ${endDate}, color: ${overlayColor}, label: ${label}}`);
    // Area highlight — the shaded band, drawn only in the default display style.
    //
    // 'lines' replaces the band with the connecting line built below. Nothing about the study's
    // report, tables or metrics depends on this: displayStyle reaches only the overlays, and the
    // date pairs it draws from are the same either way.
    if (!linesOnly) {
      addOverlay({ type: 'areaHighlight', date1: startDate, date2: endDate, color: overlayColor, label, ...params });
    }
    // Vertical lines
    // Ensure required fields for verticalLine overlays (planet1, planet2, degrees, etc.)
    const vLineParams = { ...params };
    // Use 'vLine' type (user reports this worked before)
    // Always use black for vLine overlays for maximum visibility
    // The band edges carry no caption. The vLine renderer draws labels rotated 90 degrees, so
    // "Strategy Score Study" ran vertically down every edge of every band -- once per date pair,
    // obscuring the price action the bands exist to highlight. The date went with it: it repeats
    // the axis directly below, and the study is already named in the messages panel.
    addOverlay({ ...vLineParams, type: 'vLine', date: startDate, color: 'black', label: '' });
    addOverlay({ ...vLineParams, type: 'vLine', date: endDate, color: 'black', label: '' });
    // Trendline - always use snapped dates (same as areaHighlight)
    // Always use extendedBarData for price1/price2 if available
    const trendData = extendedBarData && extendedBarData.dates && extendedBarData.ohlc ? extendedBarData : overlayData;
    const idx1 = trendData.dates.findIndex(d => d === startDate);
    const idx2 = trendData.dates.findIndex(d => d === endDate);
    console.log('[OverlayDebug] trendLine index check', { startDate, endDate, idx1, idx2, datesSample: trendData.dates.slice(Math.max(0, idx1-2), idx1+3), datesLength: trendData.dates.length });
    if (idx1 >= 0 && idx2 >= 0) {
      // Which prices the connecting line joins depends on the display style.
      //
      //   bands  the bar highs, so the line rides above the shaded band rather than through it.
      //   lines  the OPEN of the starting bar to the CLOSE of the ending bar — the move the window
      //          actually captured, which is the figure worth drawing once the band is gone.
      //
      // ohlc is [open, close, low, high]: ECharts candlestick order, not the order OHLC implies.
      const price1 = linesOnly ? trendData.ohlc[idx1][0] : trendData.ohlc[idx1][3];
      const price2 = linesOnly ? trendData.ohlc[idx2][1] : trendData.ohlc[idx2][3];
      if (typeof price1 !== 'number' || typeof price2 !== 'number') {
        console.warn(`[OverlayDebug] TrendLine price1 or price2 is undefined for startDate ${startDate}, endDate ${endDate}: price1=${price1}, price2=${price2}`);
      }
      // Standardize trendLine color: dark green for uptrend, dark red for downtrend, gray for flat
      let trendColor = '#1b5e20'; // dark green (uptrend)
      if (price2 < price1) trendColor = '#b71c1c'; // dark red (downtrend)
      else if (price2 === price1) trendColor = 'gray';
      // Remove color from params to prevent override
      let restParams = params;
      if ('color' in params) {
        // Remove color from params by deleting the property, never defining it as a variable
        restParams = { ...params };
        delete restParams.color;
      }
      addOverlay({ ...restParams, type: 'trendLine', date1: startDate, date2: endDate, price1, price2, color: trendColor });
      console.log('[OverlayDebug] Added trendLine overlay:', { ...restParams, type: 'trendLine', date1: startDate, date2: endDate, price1, price2, color: trendColor });
    } else {
      console.warn(`[OverlayDebug] Could not find indices for trendLine: startDate ${startDate} idx1=${idx1}, endDate ${endDate} idx2=${idx2}`);
      console.log('[OverlayDebug] Available trendData.dates sample:', trendData.dates.slice(0, 5), '...', trendData.dates.slice(-5));
    }
  });
  // ── Horizontal price bands between events ──
  //
  // Drawn for the Planet Retrograde study for everyone, and for EVERY area-highlight study when
  // an admin is viewing. The admin half is deliberately an exploratory tool: this file previously
  // warned against exactly that widening, on the grounds that the range between events means
  // nothing for aspect and ingress studies. That objection still stands for customers, which is
  // why the widening is role-gated rather than general — an admin looking for whether such a
  // range means anything is the person the caveat does not need protecting from.
  //
  // Emitted as `drawRect`, which already renders a markArea bounded on BOTH axes with a fill and
  // a border — that is exactly a horizontal band, so there is no new renderer to write. Bands are
  // added before the per-pair overlays above are drawn on top, so the vertical highlights and
  // trendlines stay readable through them.
  // 'lines' suppresses these too. They are shaded rectangles — the same kind of fill the style
  // exists to remove, just bounded on the price axis instead of only the time axis — so leaving
  // them in made "Lines Only" still draw shading, which is the one thing choosing it asks for.
  // They were not gated before because the style predates them for customers and, for an admin,
  // every area-highlight study draws them: an admin picking Lines Only saw shading regardless.
  const showHorizontalBands = !linesOnly && (Boolean(params.retroType) || Boolean(isAdmin));
  if (showHorizontalBands) {
    const snappedEvents = datePairs
      .map(pair => {
        let sd, ed;
        if (Array.isArray(pair)) { [sd, ed] = pair; }
        else if (pair && typeof pair === 'object') { sd = pair.startDate || pair.date1; ed = pair.endDate || pair.date2; }
        return sd && ed ? { startDate: snapToNearestDate(sd), endDate: snapToNearestDate(ed) } : null;
      })
      .filter(Boolean);

    const rawBands = computeRetrogradeBands(snappedEvents, overlayData.dates, overlayData.ohlc);
    const { fill, stroke } = bandColorsForPlanet(params.planet1);

    // Congestion is measured in bar indices, not dates: dates are strings on a category axis and
    // cannot be compared for overlap. A retrograde study rarely produces overlapping bands, but an
    // aspect study opened up to admins can produce dozens that sit on top of each other.
    const bands = annotateBandCongestion(rawBands.map(b => ({
      ...b,
      x1: overlayData.dates.indexOf(b.date1),
      x2: overlayData.dates.indexOf(b.date2),
    })));

    bands.forEach(band => {
      addOverlay({
        type: 'drawRect',
        date1: band.date1,
        price1: band.price1,
        date2: band.date2,
        price2: band.price2,
        color: fill,
        // A crowded band gets a dotted, lighter edge so its boundary stays findable among the
        // others without the stack of edges reading as one solid mass.
        borderColor: band.isCongested ? lightenStroke(stroke) : stroke,
        borderType: band.borderType,
        label: `${abbreviatePlanet(params.planet1) || params.label || 'Study'} range${band.isOpenEnded ? ' (now)' : ''}`,
        source: params.retroType ? 'retrogradeBand' : 'studyBand',
        id: `study-band-${params.planet1 || 'study'}-${band.date1}`,
      });
    });
    const congested = bands.filter(b => b.isCongested).length;
    console.log(`[OverlayDebug] Added ${bands.length} horizontal price band(s) for ${params.planet1 || params.label}`
      + ` (${congested} congested, admin=${Boolean(isAdmin)}, retro=${Boolean(params.retroType)})`);
  }

  // Messages Panel
  if (typeof updateMessagesPanel === 'function') {
    const modifiedSummary = [...(summaryLines || [])];
    console.log('[OverlayDebug] Before updateMessagesPanel:', { summaryLines: modifiedSummary, futureDates });
    updateMessagesPanel({ summaryLines: modifiedSummary, futureDates });
    console.log('[OverlayDebug] After updateMessagesPanel:', { summaryLines: modifiedSummary, futureDates });
  }

  // Journal auto-append
  if (typeof onJournalAppend === 'function') {
    onJournalAppend({
      studyLabel: params.label || 'Study',
      summaryLines: summaryLines || [],
      futureDates: futureDates || [],
      datePairCount: Array.isArray(datePairs) ? datePairs.length : 0,
    });
  }
}


// Default color palette
const defaultColors = [
  'rgba(229, 57, 53, 0.55)',    // Red
  'rgba(30, 136, 229, 0.55)',   // Blue
  'rgba(67, 160, 71, 0.55)',    // Green
  'rgba(251, 192, 45, 0.55)',   // Yellow
  'rgba(142, 36, 170, 0.55)',   // Purple
  'rgba(255, 87, 34, 0.55)',    // Orange
  'rgba(120, 144, 156, 0.55)',  // Gray
  'rgba(255, 193, 7, 0.55)',    // Amber
  'rgba(233, 30, 99, 0.55)',    // Pink
  'rgba(0, 188, 212, 0.55)',    // Cyan
  'rgba(205, 220, 57, 0.55)',   // Lime
  'rgba(96, 125, 139, 0.55)',   // Blue Gray
  'rgba(255, 152, 0, 0.55)',    // Deep Orange
  'rgba(124, 179, 66, 0.55)',   // Light Green
  'rgba(255, 255, 255, 0.55)',  // White
  'rgba(0, 0, 0, 0.55)',        // Black
  'rgba(255, 234, 0, 0.55)',    // Bright Yellow
  'rgba(0, 200, 83, 0.55)',     // Vivid Green
  'rgba(213, 0, 249, 0.55)',    // Vivid Purple
  'rgba(255, 61, 0, 0.55)',     // Vivid Red
];
